import type {
  ToothAxialLevels,
  ToothDepthMapping,
  ToothExtentsProfiles,
  ToothPlaneProfile,
  ToothRootAxis,
  ToothRootRangeMm,
} from '../config';
import {
  TOOTH_ROOT_DEPTH_SCAN_RANGE,
  TOOTH_ROOT_DEPTH_WINDOW_ROWS,
  TOOTH_ROOT_ISLAND_FLOOR_RATIO,
  TOOTH_ROOT_MIN_ISLAND_BINS,
  TOOTH_ROOT_ROWS_FALLBACK_RATIO,
} from '../config';

import {
  toothDepthRatiosBelowNeck,
  toothDepthRowOfRatio,
} from './toothDepthMapping';

type Island = {
  centreMm: number;
  mass: number;
};

const planeOf = (profiles: ToothExtentsProfiles, axis: ToothRootAxis) =>
  axis === 'md' ? profiles.mdByCr : profiles.voByCr;

const startMmOf = (profiles: ToothExtentsProfiles, axis: ToothRootAxis) =>
  profiles.axisStartsMm[axis === 'md' ? 0 : 1];

const depthOf = (
  profile: ToothPlaneProfile,
  axialLevels: ToothAxialLevels | null,
): ToothDepthMapping =>
  axialLevels?.depth ?? {
    crownOffsetMm: 0,
    fromBin: 0,
    isCrownFirst: true,
    spanMm: 0,
    toBin: profile.rowCount - 1,
  };

const collapseRowsAtRatio = ({
  depth,
  profile,
  ratio,
}: {
  depth: ToothDepthMapping;
  profile: ToothPlaneProfile;
  ratio: number;
}): number[] => {
  const centreRow = toothDepthRowOfRatio({ depth, ratio });
  const firstRow = Math.max(
    0,
    Math.min(
      profile.rowCount - TOOTH_ROOT_DEPTH_WINDOW_ROWS,
      Math.round(centreRow - (TOOTH_ROOT_DEPTH_WINDOW_ROWS - 1) / 2),
    ),
  );

  return Array.from({ length: profile.columnCount }, (_unused, column) =>
    Array.from(
      { length: TOOTH_ROOT_DEPTH_WINDOW_ROWS },
      (_unusedRow, rowOffset) =>
        profile.counts[(firstRow + rowOffset) * profile.columnCount + column],
    ).reduce((total, count) => total + (count ?? 0), 0),
  );
};

const countIslands = ({
  binMm,
  masses,
  startMm,
}: {
  binMm: number;
  masses: number[];
  startMm: number;
}): Island[] => {
  const peak = Math.max(...masses);

  if (peak <= 0) {
    return [];
  }

  const floor = peak * TOOTH_ROOT_ISLAND_FLOOR_RATIO;

  return masses
    .reduce<{ column: number; mass: number }[][]>(
      (islands, mass, column) => {
        if (mass <= floor) {
          return [...islands, []];
        }

        const started = islands.slice(0, -1);
        const current = islands[islands.length - 1] ?? [];

        return [...started, [...current, { column, mass }]];
      },
      [[]],
    )
    .filter((island) => island.length >= TOOTH_ROOT_MIN_ISLAND_BINS)
    .map((island) => {
      const mass = island.reduce((total, cell) => total + cell.mass, 0);
      const weighted = island.reduce(
        (total, cell) => total + cell.mass * (cell.column + 0.5),
        0,
      );

      return { centreMm: startMm + (weighted / mass) * binMm, mass };
    });
};

const islandsAt = ({
  axis,
  axialLevels,
  profiles,
  rangeMm,
  ratio,
}: {
  axis: ToothRootAxis;
  axialLevels: ToothAxialLevels | null;
  profiles: ToothExtentsProfiles;
  rangeMm: ToothRootRangeMm;
  ratio: number;
}): Island[] => {
  const profile = planeOf(profiles, axis);

  if (!profile?.rowCount || !profile.columnCount) {
    return [];
  }

  return countIslands({
    binMm: profiles.binMm,
    masses: collapseRowsAtRatio({
      depth: depthOf(profile, axialLevels),
      profile,
      ratio,
    }),
    startMm: startMmOf(profiles, axis),
  }).filter(
    (island) =>
      !rangeMm ||
      (island.centreMm >= rangeMm.min && island.centreMm <= rangeMm.max),
  );
};

const rootScanRatios = ({
  axialLevels,
  profile,
}: {
  axialLevels: ToothAxialLevels | null;
  profile: ToothPlaneProfile;
}) => {
  const [fromRootRatio, toRootRatio] = TOOTH_ROOT_DEPTH_SCAN_RANGE;

  return toothDepthRatiosBelowNeck({
    depth: depthOf(profile, axialLevels),
    fromRootRatio,
    neckRatio: axialLevels?.neckRatio ?? TOOTH_ROOT_ROWS_FALLBACK_RATIO,
    toRootRatio,
  });
};

const bestRootIslands = ({
  axis,
  axialLevels,
  profiles,
  rangeMm,
}: {
  axis: ToothRootAxis;
  axialLevels: ToothAxialLevels | null;
  profiles: ToothExtentsProfiles;
  rangeMm: ToothRootRangeMm;
}): Island[] => {
  const profile = planeOf(profiles, axis);

  if (!profile?.rowCount || !profile.columnCount) {
    return [];
  }

  return rootScanRatios({ axialLevels, profile }).reduce<Island[]>(
    (best, ratio) => {
      const islands = islandsAt({
        axis,
        axialLevels,
        profiles,
        rangeMm,
        ratio,
      });

      return islands.length > best.length ? islands : best;
    },
    [],
  );
};

export const resolveToothRootCentreMm = ({
  axialLevels,
  axis,
  profiles,
  rangeMm,
}: {
  axis: ToothRootAxis;
  profiles: ToothExtentsProfiles | null;
  rangeMm: ToothRootRangeMm;
  axialLevels: ToothAxialLevels | null;
}): number | null => {
  if (!profiles) {
    return null;
  }

  const islands = bestRootIslands({ axis, axialLevels, profiles, rangeMm });
  const mass = islands.reduce((total, island) => total + island.mass, 0);

  return mass > 0
    ? islands.reduce(
        (total, island) => total + island.centreMm * island.mass,
        0,
      ) / mass
    : null;
};
